import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ChevronRight,
  Download,
  ListTodo,
  MessageSquarePlus,
  Settings,
} from 'lucide-react';
import {
  BanknotesIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CurrencyRupeeIcon,
  HomeIcon,
  UserMinusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { canViewPayrollRecords } from '../hrms/pages/Payroll/payrollAccess';
import { cn } from '../lib/utils';

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

type TeamLioNavChild = {
  name: string;
  href: string;
  roles?: string[];
};

type TeamLioNavItem = {
  name: string;
  href?: string;
  icon: NavIcon;
  roles?: string[];
  gatedPath?: string;
  children?: TeamLioNavChild[];
};

const ROLE_ALL = [
  'founder',
  'director',
  'admin',
  'hr head',
  'hr admin',
  'manager',
  'supervisor',
  'employee',
  'intern',
];

export const TEAMLIO_EXPENSE_NAV_ITEMS: TeamLioNavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, roles: ROLE_ALL },
  {
    name: 'Employees',
    href: '/employees',
    icon: UsersIcon,
    roles: ['founder', 'director', 'admin', 'hr head', 'hr admin', 'manager', 'supervisor'],
  },
  { name: 'Attendance', href: '/attendance', icon: ClockIcon, roles: ROLE_ALL, gatedPath: '/employee/attendance' },
  { name: 'Leaves', href: '/leaves', icon: CalendarIcon, roles: ROLE_ALL, gatedPath: '/employee/leaves' },
  // Expense claims are self-service by default, including for newly-created
  // roles. An assigned Access profile still controls whether this is visible.
  { name: 'Expenses', href: '/expenses', icon: BanknotesIcon, gatedPath: '/expenses' },
  { name: 'Assets', href: '/assets', icon: ComputerDesktopIcon, roles: ROLE_ALL, gatedPath: '/assets' },
  {
    name: 'Task Management',
    icon: ListTodo as unknown as NavIcon,
    roles: ROLE_ALL,
    children: [
      { name: 'Daily Planner', href: '/todo' },
      { name: 'Projects', href: '/projects' },
      { name: 'Tasks', href: '/tasks/list' },
      { name: 'Clients', href: '/clients' },
      { name: 'Vendors', href: '/vendors' },
      { name: 'Reports', href: '/tasks/reports' },
    ],
  },
  {
    name: 'Work Flow',
    icon: BriefcaseIcon,
    roles: ROLE_ALL,
    children: [
      { name: 'Work Schedule', href: '/screen-recording', roles: ['founder', 'director', 'admin', 'hr head', 'hr admin', 'manager'] },
      { name: 'Activity Reports', href: '/activity-reports' },
      { name: 'Activity Comparison', href: '/activity-comparison', roles: ['founder', 'director', 'admin', 'hr head', 'hr admin', 'manager'] },
    ],
  },
  {
    name: 'Payroll',
    icon: CurrencyRupeeIcon,
    roles: ROLE_ALL,
    children: [
      { name: 'Payroll Records', href: '/my-payroll' },
      // Self-service — no panelCatalog key in TeamLio, so every role keeps it.
      { name: 'Tax Summary', href: '/employee/payroll/tax' },
      { name: 'Payroll Approval', href: '/employee/payroll/approval', roles: ['founder', 'director', 'admin', 'hr head', 'hr admin', 'manager'] },
      { name: 'Reports', href: '/employee/payroll/reports' },
    ],
  },
  { name: 'Downloads', href: '/downloads', icon: Download as unknown as NavIcon, roles: ROLE_ALL },
  {
    name: 'Separation Management',
    href: '/separation/dashboard',
    icon: UserMinusIcon,
    roles: ['founder', 'director', 'admin', 'hr head', 'hr admin'],
  },
];

function normalizedRole(role?: string): string {
  const next = (role || 'employee').toLowerCase();
  return next === 'intern' ? 'employee' : next;
}

function formatRole(role?: string): string {
  return (role || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function useForceLightTeamLioTheme() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const previousTheme = root.getAttribute('data-theme');
    root.setAttribute('data-theme', 'light');

    return () => {
      if (previousTheme) root.setAttribute('data-theme', previousTheme);
      else root.removeAttribute('data-theme');
    };
  }, []);
}

interface TeamLioExpenseShellProps {
  children: ReactNode;
}

export default function TeamLioExpenseShell({ children }: TeamLioExpenseShellProps) {
  useForceLightTeamLioTheme();

  const { user } = useAuth();
  const { hasAccess, isPanelVisible, isPathVisible } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);
  const role = normalizedRole(user?.role);
  const canShowPayrollRecords = canViewPayrollRecords(user?.role, {
    hasAccess,
    isVisible: isPanelVisible('payroll-records'),
  });

  const visibleItems = useMemo(() => {
    const roleOk = (roles?: string[]) => !roles || roles.includes(role);
    const pathOk = (path?: string) => !hasAccess || !path || isPathVisible(path);

    return TEAMLIO_EXPENSE_NAV_ITEMS
      .map((item) => {
        if (!roleOk(item.roles)) return null;
        if (!item.children) return pathOk(item.gatedPath || item.href) ? item : null;

        const children = item.children.filter((child) =>
          roleOk(child.roles) &&
          pathOk(child.href) &&
          (child.href !== '/my-payroll' || canShowPayrollRecords)
        );
        return children.length ? { ...item, children } : null;
      })
      .filter(Boolean) as TeamLioNavItem[];
  }, [canShowPayrollRecords, hasAccess, isPathVisible, role]);

  const userName = user?.name || 'Azmat Admin';
  const displayRole = formatRole(user?.role || 'admin');

  return (
    <div className="gt-themed teamlio-expense-shell" data-testid="teamlio-expense-shell">
      <aside className={cn('gt-sidebar teamlio-expense-sidebar', isMinimized && 'is-collapsed')}>
        <div className={cn('gt-sidebar-header', isMinimized ? 'justify-center' : 'justify-between')}>
          {!isMinimized && (
            <div className="gt-sidebar-identity">
              <span className="gt-sidebar-user" title={userName}>
                {userName}
              </span>
              <span className="gt-sidebar-role">({displayRole})</span>
              <span className="gt-sidebar-company">TeamLio</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsMinimized((value) => !value)}
            className="gt-sidebar-collapse hidden md:inline-flex"
            aria-label={isMinimized ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isMinimized ? (
              <ChevronDoubleRightIcon className="h-4 w-4" />
            ) : (
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            )}
          </button>
        </div>

        <nav className="gt-sidebar-nav" aria-label="TeamLio expense navigation">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const childActive = item.children?.some((child) => location.pathname.startsWith(child.href));
            const isActive = item.href
              ? location.pathname === item.href || location.pathname.startsWith(item.href + '/')
              : Boolean(childActive);

            if (item.children) {
              return (
                <div key={item.name} className="gt-sidebar-item-wrap relative">
                  <button
                    type="button"
                    className={cn('gt-sidebar-item', isActive && 'is-active')}
                    aria-expanded={isActive}
                    title={isMinimized ? item.name : undefined}
                    onClick={() => {
                      if (isMinimized) setIsMinimized(false);
                      else if (item.children?.[0]) navigate(item.children[0].href);
                    }}
                  >
                    <Icon className="gt-sidebar-icon" />
                    {!isMinimized && (
                      <>
                        <span className="gt-sidebar-label">{item.name}</span>
                        {isActive ? (
                          <ChevronDown className="gt-sidebar-chevron" />
                        ) : (
                          <ChevronRight className="gt-sidebar-chevron" />
                        )}
                      </>
                    )}
                  </button>
                  {!isMinimized && isActive && (
                    <div className="gt-sidebar-submenu">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={cn(
                            'gt-sidebar-subitem',
                            location.pathname === child.href && 'is-active'
                          )}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                  {isMinimized && <span className="gt-sidebar-tooltip">{item.name}</span>}
                </div>
              );
            }

            return (
              <div key={item.name} className="gt-sidebar-item-wrap relative">
                <NavLink
                  to={item.href || '/expenses'}
                  className={({ isActive: linkActive }) =>
                    cn('gt-sidebar-item', (linkActive || isActive) && 'is-active')
                  }
                  title={isMinimized ? item.name : undefined}
                >
                  <Icon className="gt-sidebar-icon" />
                  {!isMinimized && <span className="gt-sidebar-label">{item.name}</span>}
                </NavLink>
                {isMinimized && <span className="gt-sidebar-tooltip">{item.name}</span>}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="teamlio-expense-shell__main">
        <header className="gt-topbar teamlio-expense-topbar">
          <div className="teamlio-expense-ticker">
            <strong>Quality Approved, no findings. Marking complete, dispatching Task 4 (access key + settings</strong>
            <button type="button">View more</button>
          </div>

          <div className="gt-topbar-actions">
            <button
              type="button"
              className="gt-topbar-action"
              title="Daily Planner"
              aria-label="Daily Planner"
              onClick={() => navigate('/todo')}
            >
              <ListTodo size={18} />
            </button>
            <button
              type="button"
              className="gt-topbar-action"
              title="New Message"
              aria-label="New Message"
              onClick={() => navigate('/chat')}
            >
              <MessageSquarePlus size={18} />
            </button>
            <button type="button" className="gt-topbar-action teamlio-expense-bell" aria-label="Notifications">
              <Bell size={19} />
              <span>12</span>
            </button>
            <button type="button" className="gt-topbar-action" aria-label="Settings">
              <Settings size={19} />
            </button>
          </div>
        </header>

        <main className="teamlio-expense-shell__content">
          {children}
        </main>
      </div>
    </div>
  );
}
